import axios, { AxiosResponse, isAxiosError } from "axios";
import { Mancha } from "../../context";
import { RequestError } from "./api";
import { MultiPolygon, Polygon } from "geojson";
import { PolyMancha } from "../../components";
import { LatLngExpression } from "leaflet";

const add = async (
  mancha: PolyMancha,
  onProgress: (loaded: number, total: number) => void,
  chunkSize = 0.5 * 1024 * 1024
): Promise<PolyMancha | RequestError> => {
  try {

    const dados = {
      id: mancha.properties?.id,
      idBarragem: mancha.properties?.idBarragem,
      nome: mancha.properties?.nome,
      descricao: mancha.properties?.descricao,
      idUpdate: "",
      dataCadastro: new Date(),
      polygon: {
        type: "Feature",
        geometry: mancha.toGeoJSON().geometry,
        properties: { style: mancha.paintOptions },
      },
    };

    const jsonStr = JSON.stringify(dados);
    const totalChunks = Math.ceil(jsonStr.length / chunkSize);

    let request;
    let chunkId = "|";
    for (let chunkNumber = 0; chunkNumber < totalChunks; chunkNumber++) {
      const start = chunkNumber * chunkSize;
      const end = Math.min(start + chunkSize, jsonStr.length);
      const chunk = jsonStr.slice(start, end);

      const formData = new FormData();
      formData.append("chunk", new Blob([chunk], { type: "application/json" }));
      formData.append("chunk_number", chunkNumber.toString());
      formData.append("total_chunks", totalChunks.toString());

      // Enviar cada chunk
      request = await axios.post<Mancha | { chunkId: string; }>(
        `${process.env.REACT_APP_URL}/manchas/${chunkId}`,
        formData,
        {
          onUploadProgress: (progressEvent) => {
            const chunksSent = chunkNumber;
            const currentChunkProgress =
              progressEvent.loaded / (progressEvent.total ?? 1);
            const currentProgress = chunksSent + currentChunkProgress;
            onProgress(currentProgress, totalChunks);
          },
        }
      );

      if ("chunkId" in request.data) {
        chunkId = request.data.chunkId;
      }
    }

    if (!request || "chunkId" in request.data) {
      return {
        error: "Problemas ao atualizar dados",
        message: "Nem todos os chunks foram enviados!",
      };
    }

    return processMancha(request.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Mancha Error",
        message:
          error.response?.data.message ?? "Erro ao adicionar mancha",
      };
    } else {
      throw error;
    }
  }
};

const get = async (id: number): Promise<PolyMancha | RequestError> => {
  try {
    const data: AxiosResponse<Mancha> = await axios.get(
      `${process.env.REACT_APP_URL}/manchas/${id}`
    );
    return processMancha(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Mancha Error",
        message: error.response?.data.message ?? "Erro ao obter dados da Mancha",
      };
    } else {
      throw error;
    }
  }
};

const getTable = async (id: number): Promise<PolyMancha | RequestError> => {
  try {
    const data: AxiosResponse<Mancha> = await axios.get(
      `${process.env.REACT_APP_URL}/manchas/table/${id}`
    );
    return processMancha(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Mancha Error",
        message: error.response?.data.message ?? "Erro ao obter dados da Mancha",
      };
    } else {
      throw error;
    }
  }
};

const remove = async (id: number): Promise<PolyMancha | RequestError> => {
  try {
    const data: AxiosResponse<Mancha> = await axios.delete(
      `${process.env.REACT_APP_URL}/manchas/${id}`
    );
    return processMancha(data.data);
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: error.response?.data.error ?? "Mancha Error",
        message: error.response?.data.message ?? "Erro ao excluir da Mancha",
      };
    } else {
      throw error;
    }
  }
};

// Processar dados da Mancha
const processMancha = (dados: Mancha): PolyMancha => {
  const dataCadastro = new Date(dados.dataCadastro);
  let dataModificacao = undefined;
  if (dados.dataModificacao) {
    dataModificacao = new Date(dados.dataModificacao);
  }

  return new PolyMancha(
    invertCoords(dados.polygon.geometry) as LatLngExpression[][] | LatLngExpression[][][],
    {
      ...dados,
      dataCadastro: dataCadastro,
      dataModificacao: dataModificacao,
    },
    dados.polygon.properties.style
  );
};

// Inverter as coordenadas do polígono
const invertCoords = (layer: Polygon | MultiPolygon) => {
  if (layer.type === "MultiPolygon") {
    return layer.coordinates.map((coords) => {
      return coords.map((coord) => {
        return coord.map(coord => [coord[1], coord[0]]);
      });
    });
  }

  return layer.coordinates.map((coords) => coords.map(coord => [coord[1], coord[0]]));
};

export const ApiManchas = {
  add,
  get,
  remove,
  getTable,
};
